import React, { useContext, useEffect, useCallback } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import type { Timeline, Marker, Timespan, Photo } from '@app-types/entity_types'
import { convertViewThemeToCSSProperties } from '@utils/theme'
import Provider from '../provider'
import { useHorizontalTimelineContext } from '../context'
import { ThemeContext } from '@base/ThemeContext'
import { initialize, changeSize } from '../actions'
import { minWidthRatioSelector } from '../selectors/scroller_selectors'

import ScrubberDisplayContainer from '../containers/ScrubberDisplayContainer'
import TimelineDisplayContainer from '../containers/TimelineDisplayContainer'

type HorizontalTimelineProps = {
  timeline: Timeline
  markers: Marker[]
  timespans: Timespan[]
  photos: Record<number, Photo>
}

const HorizontalTimeline: React.FC<HorizontalTimelineProps> = props => {
  const { timeline, markers, timespans, photos } = props
  const { state, dispatch } = useHorizontalTimelineContext()
  const theme = useContext(ThemeContext)

  const { initialized } = state

  useEffect(() => {
    // calculate the minimum width ratio, then use that to set initial left/right values
    // this is because we may not want to allow the user to zoom out more as events would appear outside the bounds of the surface available
    if (!state.initialized && state.size.width > 0 && state.size.height > 0) {
      const minWidthRatio = minWidthRatioSelector(state, props)
      dispatch(initialize(minWidthRatio))
    }
  }, [state.initialized, state.size.width, state.size.height])

  const handleResize = useCallback((measuredWidth: number | undefined, measuredheight: number | undefined) => {
    if (measuredWidth && measuredWidth > 0 && measuredheight && measuredheight > 0) {
      dispatch(changeSize(measuredWidth, measuredheight))
    }
  }, [])

  const { ref } = useResizeDetector({
    onResize: handleResize,
  })

  const haveDimensionsInState = state.size.width > 0 && state.size.height > 0
  let content = null
  const readyToRender = initialized && haveDimensionsInState
  if (readyToRender) {
    // turn our theme into css varibales we can set on the component
    const themeStyle: React.CSSProperties = convertViewThemeToCSSProperties(theme)

    content = (
      <div className="timeline timeline--horizontal" style={themeStyle}>
        <div className="timeline__content">
          <ScrubberDisplayContainer markers={markers} timespans={timespans} />
          <TimelineDisplayContainer timeline={timeline} markers={markers} timespans={timespans} photos={photos} />
        </div>
      </div>
    )
  }

  return (
    <div id="timeline-fullscreenable" className="full-size-scrollable resizable-content" ref={ref}>
      {content}
    </div>
  )
}

const Provided = (props: HorizontalTimelineProps) => {
  return (
    <Provider>
      <HorizontalTimeline {...props} />
    </Provider>
  )
}

export default Provided
